import { useState, useCallback } from 'react';
import { lookupPincode, PincodeData, isValidPincode } from '../services/pincodeService.ts';

export interface UsePincodeLookupReturn {
  lookupPincode: (pincode: string) => Promise<void>;
  pincodeData: PincodeData | null;
  loading: boolean;
  error: string | null;
  isValid: boolean;
  clearData: () => void;
}

/**
 * Custom hook for pincode lookup functionality
 * @returns Object with lookup function and state
 */
export const usePincodeLookup = (): UsePincodeLookupReturn => {
  const [pincodeData, setPincodeData] = useState<PincodeData | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isValid, setIsValid] = useState(false);

  const handleLookup = useCallback(async (pincode: string) => {
    // Clear previous data
    setError(null);
    setPincodeData(null);
    
    // Validate pincode format
    const valid = isValidPincode(pincode);
    setIsValid(valid);
    
    if (!valid) {
      if (pincode.length > 0) {
        setError('Please enter a valid 6-digit pincode');
      }
      return;
    }

    setLoading(true);
    
    try {
      const data = await lookupPincode(pincode);
      
      if (data) {
        setPincodeData(data);
        setError(null);
      } else {
        setError('Pincode not found. Please check and try again.');
        setPincodeData(null);
      }
    } catch (err) {
      setError('Failed to lookup pincode. Please try again.');
      setPincodeData(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const clearData = useCallback(() => {
    setPincodeData(null);
    setError(null);
    setLoading(false);
    setIsValid(false);
  }, []);

  return {
    lookupPincode: handleLookup,
    pincodeData,
    loading,
    error,
    isValid,
    clearData,
  };
};
